<template>
	<v-sheet rounded>
		<v-card color="#125B92">
			<v-card-title v-if="titleBar" class="align-center">
				<v-icon left large color="#E3ECF2">mdi-folder</v-icon>
				<h3 class="white--text">{{ title }}</h3>
				<v-spacer />
				<slot />
			</v-card-title>

			<v-divider v-if="titleBar" class="thick" />

			<v-data-table
				:headers="tableHeaders"
				:items="items"
				:loading="loading"
				:items-per-page.sync="filters.paginated"
				:footer-props="{
					'items-per-page-options': [10, 20, 30, 40, 50]
				}"
				:page.sync="filters.page"
				:server-items-length="totalClients"
				:sort-by.sync="filters.sort"
				:sort-desc.sync="filters.descending"
				class="table-stripped"
				@update:page="$vuetify.goTo(0)">
				<template #top>
					<v-toolbar flat>
						<v-spacer />
						<div class="mx-3 datatable-filter-small">
							<v-select
								v-model="filters.active"
								:items="active"
								placeholder="Filter by Active"
								single-line
								clearable
								filled
								dense
								outlined
								hide-details></v-select>
						</div>
						<div class="flex-fill datatable-filter-large">
							<v-text-field
								v-model.trim="filters.search"
								class="table-search"
								append-icon="mdi-magnify"
								placeholder="Search..."
								name="search"
								autocomplete="off"
								single-line
								clearable
								filled
								dense
								outlined
								full-width
								autofocus
								hide-details />
						</div>
						<div class="ml-3">
							<v-chip>{{ totalClients | numeric }}</v-chip>
						</div>
					</v-toolbar>
				</template>
				<template #item.name="{ item }">
					<Link :href="route('clients.show', item)">{{ item.name }}</Link>
				</template>

				<template #item.address="{ item }">
					{{ item.address1 }}, {{ item.address2 }}<br />
					{{ item.city }}, {{ item.state.code }} {{ item.zipcode }}
				</template>

				<template #item.billing_contact_name="{ item }">
					{{ item.billing_contact_name }}
				</template>

				<template #item.billing_contact_phone="{ item }">
					{{ item.billing_contact_phone }}
				</template>

				<template #item.projects_count="{ item }">
					<Link :href="'/projects?search=&status=&client=' + item.id + '&paginated=30&page=1&sort=id&descending=true'" title="View Projects">
						<v-chip>{{ item.projects_count }}</v-chip>
					</Link>
				</template>

				<template #item.invoices_count="{ item }">
					<Link :href="'/invoices?search=' + item.name + '&paginated=30&page=1&status=&sort=id&descending=true'" title="View Invoices">
						<v-chip>{{ item.invoices_count }}</v-chip>
					</Link>
				</template>

				<template #item.users_count="{ item }">
					<Link :href="'/users?search=' + item.name + '&status=&type=&paginated=30&page=1&sort=first_name&descending=false'" title="View Users">
						<v-chip>{{ item.users_count }}</v-chip>
					</Link>
				</template>

				<template #item.billing_contact_email="{ item }">
					<a :href="'mailto:' + item.billing_contact_email">{{ item.billing_contact_email }}</a>
				</template>

				<template #item.actions="{ item }">
					<td class="actions text-end">
						<v-dialog transition="scroll-x-reverse-transition" max-width="1100">
							<template #activator="{ on, attrs }">
								<v-btn icon
									small
									model="clients"
									:model-id="item.id"
									v-bind="attrs"
									v-on="on">
									<v-icon color="secondary">
										mdi-pencil
									</v-icon>
								</v-btn>
							</template>
							<template #default="dialogEdit">
								<v-card>
									<v-card-title>
										<div>Edit Client</div>
										<v-icon right class="ml-auto theme--dark" @click="dialogEdit.value = false">mdi-close</v-icon>
									</v-card-title>
									<v-divider class="mb-5" />
									<v-card-text>
										<client-form :client="item" @success="dialogEdit.value = false" />
									</v-card-text>
								</v-card>
							</template>
						</v-dialog>
						<delete-button small icon model="clients" :model-id="item.id">
							<v-icon color="danger">
								mdi-delete
							</v-icon>
						</delete-button>
					</td>
				</template>
			</v-data-table>
		</v-card>
	</v-sheet>
</template>
<script>
import _ from 'lodash'
import ClientForm from './Form'
import DeleteButton from '@/Pages/Backend/Components/DeleteButton'

export default {
	components: {DeleteButton, ClientForm},
	props: {
		title: {
			type: String,
			default: 'Clients'
		},
		clients: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		simplify: {
			type: Boolean,
			default: () => false
		},
		titleBar: {
			type: Boolean,
			default: () => false
		}
	},
	data () {
		return {
			filters: {
				search: '',
				active: null,
				paginated: 30,
				page: 1,
				descending: false,
				sort: 'name'
			},
			loading: false,
			items: this.clients.data,
			tableHeaders: [
				{
					text: 'Name',
					align: 'start',
					value: 'name'
				},
				{
					text: 'Projects',
					sortable: false,
					align: 'center',
					value: 'projects_count'
				},
				{
					text: 'Invoices',
					sortable: false,
					align: 'center',
					value: 'invoices_count'
				},
				{
					text: 'Contacts',
					sortable: false,
					align: 'center',
					value: 'users_count'
				},
				{
					text: 'Address',
					sortable: false,
					align: 'start',
					value: 'address'
				},
				{
					text: 'Billing Contact',
					align: 'start',
					value: 'billing_contact_name'
				},
				{
					text: 'Phone',
					sortable: false,
					align: 'start',
					value: 'billing_contact_phone'
				},
				{
					text: 'Email',
					align: 'start',
					value: 'billing_contact_email'
				},
				{
					text: '',
					sortable: false,
					align: 'center',
					value: 'actions'
				}
			],
			active: [
				{ text: 'Active', value: 'true' },
				{ text: 'Inactive', value: 'false' }
			]
		}
	},
	computed: {
		totalClients () {
			if (this.clients && this.clients.hasOwnProperty('meta')) {
				return this.clients.meta.total
			}

			return 0
		}
	},
	watch: {
		'filters.search': {
			handler: _.debounce(function (newVal, oldVal) {
				if (newVal && newVal !== oldVal || !newVal && oldVal) {
					this.getClients()
				}
			}, 350)
		},
		clients () {
			this.items = this.clients.data
		},
		dialog (val) {
			val || this.close()
		}
	},
	mounted () {
		this.filters = this.$page.props.filters

		this.$watch(vm => [vm.filters.active, vm.filters.paginated, vm.filters.page, vm.filters.sort, vm.filters.descending], val => {
			this.getClients()
		})
	},
	methods: {
		getClients () {
			this.$inertia.reload({
				onStart: () => {
					this.loading = true
				},
				onFinish: () => {
					this.loading = false
					this.items = this.clients.data

					if(! this.length(this.items) && this.filters.page > 1) {
						this.filters.page = 1
					}
				},
				only: ['clients'],
				data: this.filters
			})
		}
	}
}
</script>
